<!--
 * @Description: 模板管理页面
 * @Author: mdb
 * @Date: 2022-06-21 10:30:47
 * @LastEditTime: 2023-04-20 13:31:41
 * @LastEditors: mdb
-->
<template>
  <CosFullLayout class="tpl-container">
    <template #aside>
      <CosFixFooterPage :autoFooter="false">
        <div class="tpl-sidebar">
          <HeaderTitle :title="t('marketing.模板管理')" />
          <div class="tpl-sidebar_search">
            <a-input
              v-model:value.trim="queryForm.keyword"
              :maxlength="100"
              :placeholder="t('marketing.模板名称/说明')"
              @pressEnter="getList()"
            />
            <a-button :class="{ 'no-border': isAdd }" @click="toggleList">
              <template #icon>
                <img src="@/assets/marketing/edit.svg" v-if="!isAdd" />
                <span class="iconfont icon-baocuo" v-else />
              </template>
            </a-button>
          </div>
          <ul class="tpl-sidebar_list">
            <li
              class="tpl-sidebar_list__item"
              :class="{ active: isActiveIndex === index && currentId }"
              v-for="(item, index) in listData"
              :key="index"
            >
              <p @click="handleClick(index, item.id)">{{ `${index + 1}、${item.name}` }}</p>
              <img
                v-permission="permission.del"
                v-if="isAdd"
                :src="isActiveIndex === index ? minusCircleWhite : minusCircleBlack"
                @click="handleTplDelete(item)"
              />
            </li>
          </ul>
          <dc-layout-empty type="nothing" v-if="!listData?.length" />
          <div v-permission="permission.add" class="tpl-add-button" v-if="isAdd" @click="handleTplAdd()">
            <img src="@/assets/marketing/add-circle.svg" />
            <p>{{ t('marketing.新增模板') }}</p>
          </div>
        </div>
        <template #footer>
          <a-pagination
            size="small"
            style="padding-bottom: 10px"
            v-if="listData?.length"
            v-model:current="queryForm.pageNumber"
            v-model:pageSize="queryForm.pageSize"
            :total="pageTotal"
            :show-total="(total:number) => t('marketing.共个模板', {total: total})"
            @change="handleCurrentChange"
          />
        </template>
      </CosFixFooterPage>
    </template>
    <template #main>
      <CosFixFooterPage>
        <div class="tpl-content_main">
          <HeaderTitle :title="t('marketing.模板详情')" />
          <MainForm
            :subdomain="subdomain"
            :templateId="currentId"
            :status="tplStatus"
            ref="mainForm"
            @submit="onSubmit"
          />
        </div>
        <template #footer>
          <dc-layout-button-group>
            <a-button
              v-permission="permission.edit"
              type="primary"
              @click="handleEdit()"
              v-if="tplStatus === 'view' && listData?.length"
              >{{ t('btn.edit')}}</a-button
            >
            <template v-if="tplStatus !== 'view'">
              <a-button type="primary" @click="handleSubmit()">{{ t('btn.save')}}</a-button>
              <a-button @click="handleCancel()">{{ t('btn.cancel')}}</a-button>
            </template>
          </dc-layout-button-group>
        </template>
      </CosFixFooterPage>
    </template>
  </CosFullLayout>
</template>

<script setup lang="ts">
import { ref, reactive, toRaw, onBeforeMount, PropType } from 'vue'
import HeaderTitle from './components/HeaderTitle.vue'
import MainForm from './components/MainForm.vue'
import CosFullLayout from '@/components/CosFullLayout/index.vue'
import CosFixFooterPage from '@/components/CosFullLayout/fixFooterPage.vue'
import minusCircleWhite from '@/assets/marketing/minus-circle-white.svg'
import minusCircleBlack from '@/assets/marketing/minus-circle-black.svg'
import Modal from '@/hooks/modalType'
import { message } from '@dc-platform/dc-components'
import { templateList, templateCreate, templateUpdate, templateDelete } from '@/api/marketing/marketingManage'
import type { TemplateParams, TemplateListItem, TemplateEditParams } from '@/typings/api/marketingManage'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

defineProps({
  subdomain: {
    type: String as PropType<'fbu' | 'lbu'>,
    required: true
  },
  permission: {
    type: Object as PropType<{
      add: string
      edit: string
      del: string
    }>,
    required: true
  }
})

const listData = ref<TemplateListItem[]>()
const pageTotal = ref<number>(0)
const queryForm = reactive<TemplateParams>({
  pageNumber: 1,
  pageSize: 10,
  keyword: ''
})
const getList = (pageNumber = 1) => {
  const params = toRaw({ ...queryForm, pageNumber })
  templateList(params).then((res) => {
    listData.value = res.data.items
    queryForm.pageNumber = res.data.pageNumber
    queryForm.pageSize = res.data.pageSize
    pageTotal.value = res.data.totalCount
    if (res.data.items?.length) {
      currentId.value = res.data.items[isActiveIndex.value].id
    }
  })
}
const handleCurrentChange = (page: number) => getList(page)
onBeforeMount(getList)

const tplStatus = ref<'view' | 'edit' | 'add'>('view')
const isAdd = ref<boolean>(false)
const isActiveIndex = ref<number>(0)
const currentId = ref<number>()
const handleTplDelete = (row: TemplateListItem) => {
  Modal.delete({
    name: row.name,
    onOk() {
      templateDelete({ templateId: row.id }).then((res) => {
        message.success(t('marketing.删除模板成功'))
        isActiveIndex.value = 0
        getList()
      })
    }
  })
}
const handleTplAdd = () => {
  currentId.value = undefined
  isAdd.value = false
  tplStatus.value = 'edit'
}
const toggleList = () => {
  isAdd.value = !isAdd.value
}
const handleClick = (index: number, id: number) => {
  tplStatus.value = 'view'
  isActiveIndex.value = index
  currentId.value = id
}

const handleEdit = () => {
  tplStatus.value = 'edit'
}
const handleCancel = () => {
  tplStatus.value = 'view'
  if (listData.value?.length) {
    currentId.value = listData.value[isActiveIndex.value]?.id
  } else {
    currentId.value = undefined
  }
}

const mainForm = ref()
const handleSubmit = () => {
  mainForm.value.handleSubmit()
}
const onSubmit = async (data: TemplateEditParams) => {
  const params = Object.assign({}, data)
  params.config = JSON.stringify(params.config)
  params.globalConfig = JSON.stringify(params.globalConfig)
  delete params.fileList
  try {
    if (params.templateId) {
      await templateUpdate(params)
      message.success(t('marketing.编辑模板成功'))
    } else {
      await templateCreate(params)
      message.success(t('marketing.新增模板成功'))
    }
    currentId.value = undefined
    tplStatus.value = 'view'
    getList()
  } catch (error) {
    console.log(error)
  }
}
</script>
<script lang="ts">
export default { name: 'DcCosZbuTemplateManage' }
</script>

<style lang="scss" scoped>
$color-bg: #fff;

.tpl-container {
  .tpl-sidebar {
    position: relative;
    margin-right: 8px;
    background: $color-bg;
    .tpl-sidebar_search {
      display: flex;
      :deep(.ant-input) {
        text-align: center;
      }
      :deep(.ant-input) {
        margin-right: 16px;
        padding: 0 11px;
      }
      :deep(.ant-btn-icon-only) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 30px;
        padding: 0;
        font-size: 0;
        .iconfont {
          font-size: 15px;
        }
        &.no-border {
          border: none;
        }
      }
    }
    .tpl-sidebar_list {
      .tpl-sidebar_list__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 36px;
        line-height: 36px;
        margin-top: 12px;
        padding-left: 8px;
        box-shadow: 0px 0.5px 0px #d8dfe4;
        cursor: pointer;
        > p {
          flex: 1;
        }
        img {
          padding: 8px 11px;
        }
        &.active {
          color: #ffffff;

          background: $Primary;
          border-radius: 4px 0px 0px 4px;
          img {
            color: #ffffff;
          }
        }
      }
    }
    .tpl-add-button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 36px;
      line-height: 1;
      border: 1px dashed #d8dfe4;
      cursor: pointer;
      transition: all 0.3s;
      &:hover {
        color: $Primary;
        border-color: $Primary;
      }
      p {
        margin-left: 11px;
      }
    }
    :deep(.ant-pagination) {
      padding-bottom: 10px;
    }
  }
  .tpl-content_main {
    flex: 1;
    background: $color-bg;
  }
  // .tpl-content {
  //   display: flex;
  //   flex-direction: column;
  //   flex: 1;
  //   height: 100%;
  //   overflow-y: auto;

  //   .tpl-content_footer {
  //     display: flex;
  //     align-items: center;
  //     justify-content: center;
  //     height: 64px;
  //     margin-top: 8px;
  //     background: $color-bg;
  //   }
  // }
}

.dc-layout-empty {
  :deep(.ant-empty-image) {
    height: 200px;
  }
}
</style>
